/*
 * @Copyright 2020. Institute for Future Intelligence, Inc.
 * @author Charles Xie
 */

import $ from "jquery";
import {closeAllContextMenus, flowchart, isNumber} from "../../Main";
import {BlockContextMenu} from "./BlockContextMenu";
import {RainbowHatBlock} from "../RainbowHatBlock";
import {PngSaver} from "../../tools/PngSaver";
import {Util} from "../../Util";

export class RainbowHatBlockContextMenu extends BlockContextMenu {

  constructor() {
    super();
    this.id = "rainbow-hat-block-context-menu";
  }

  getUi(): string {
    return `<menu id="${this.id}" class="menu" style="width: 200px; z-index: 10000">
              <li class="menu-item">
                <button type="button" class="menu-btn" id="${this.id}-copy-button"><i class="fas fa-copy"></i><span class="menu-text">Copy</span></button>
              </li>
              <li class="menu-item">
                <button type="button" class="menu-btn" id="${this.id}-delete-button"><i class="fas fa-trash"></i><span class="menu-text">Delete</span></button>
              </li>
              <li class="menu-item">
                <button type="button" class="menu-btn" id="${this.id}-clear-sensor-data-button"><i class="fas fa-eraser"></i><span class="menu-text">Clear Sensor Data</span></button>
              </li>
              <li class="menu-separator"></li>` + this.getLayerMenu() + `<li class="menu-separator"></li>
              <li class="menu-item">
                <button type="button" class="menu-btn" id="${this.id}-save-image-button"><i class="fas fa-camera"></i><span class="menu-text">Save Image</span></button>
              </li>
              <li class="menu-item">
                <button type="button" class="menu-btn" id="${this.id}-properties-button"><i class="fas fa-cog"></i><span class="menu-text">Properties</span></button>
              </li>
            </menu>`;
  }

  addListeners(): void {
    super.addListeners();
    let clearSensorDataButton = document.getElementById(this.id + "-clear-sensor-data-button");
    clearSensorDataButton.addEventListener("click", this.clearSensorDataButtonClick.bind(this), false);
    let saveImageButton = document.getElementById(this.id + "-save-image-button");
    saveImageButton.addEventListener("click", this.saveImageButtonClick.bind(this), false);
  }

  private clearSensorDataButtonClick(): void {
    // FIXME: This event will not propagate to its parent. So we have to call this method here to close context menus.
    closeAllContextMenus();
    (<RainbowHatBlock>this.block).clearSensorData();
    flowchart.blockView.requestDraw();
  }

  private saveImageButtonClick(): void {
    // FIXME: This event will not propagate to its parent. So we have to call this method here to close context menus.
    closeAllContextMenus();
    PngSaver.saveAs((<RainbowHatBlock>this.block).toCanvas());
  }

  protected getPropertiesUI(): string {
    let rainbow = <RainbowHatBlock>this.block;
    let rgbLedHtml: string = "";
    for (let i = 0; i < rainbow.rgbLedLights.length; i++) {
      rgbLedHtml += "<tr><td>RGB LED Light</td><td>" + rainbow.rgbLedLights[i].name + "</td><td>" + rainbow.rgbLedLights[i].color + "</td></tr>";
    }
    return `<div style="font-size: 90%; overflow-y: auto;">
              <table class="w3-table-all w3-left w3-hoverable">
                <tr>
                  <td>Remote Key:</td>
                  <td colspan="2"><input type="text" id="rainbow-hat-remote-key-field" style="width: 100%" spellcheck="false"></td>
                </tr>
                <tr>
                  <td>Width:</td>
                  <td colspan="2"><input type="text" id="rainbow-hat-width-field" style="width: 100%"></td>
                </tr>
                <tr>
                  <td>Height:</td>
                  <td colspan="2"><input type="text" id="rainbow-hat-height-field" style="width: 100%"></td>
                </tr>
              </table>
              <table class="w3-table-all w3-left w3-hoverable">
                <thead>
                  <tr class="w3-gray">
                  <th>Component</th>
                  <th>Name</th>
                  <th>Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Momentary Button A</td>
                    <td>${rainbow.buttonA.name}</td>
                    <td>${rainbow.buttonA.on}</td>
                  </tr>
                  <tr>
                    <td>Momentary Button B</td>
                    <td>${rainbow.buttonB.name}</td>
                    <td>${rainbow.buttonB.on}</td>
                  </tr>
                  <tr>
                    <td>Momentary Button C</td>
                    <td>${rainbow.buttonC.name}</td>
                    <td>${rainbow.buttonC.on}</td>
                  </tr>
                  <tr>
                    <td>Red LED Light</td>
                    <td>${rainbow.redLedLight.name}</td>
                    <td>${rainbow.redLedLight.on}</td>
                  </tr>
                  <tr>
                    <td>Green LED Light</td>
                    <td>${rainbow.greenLedLight.name}</td>
                    <td>${rainbow.greenLedLight.on}</td>
                  </tr>
                  <tr>
                    <td>Blue LED Light</td>
                    <td>${rainbow.blueLedLight.name}</td>
                    <td>${rainbow.blueLedLight.on}</td>
                  </tr>
                  <tr>
                    <td>Temperature Sensor</td>
                    <td>${rainbow.temperatureSensor.name}</td>
                    <td>${rainbow.temperatureSensor.getNumberOfDataPoints() > 0 ? rainbow.temperatureSensor.getLatestData().toFixed(2) : "NA"}</td>
                  </tr>
                  <tr>
                    <td>Barometric Pressure Sensor</td>
                    <td>${rainbow.barometricPressureSensor.name}</td>
                    <td>${rainbow.barometricPressureSensor.getNumberOfDataPoints() > 0 ? rainbow.barometricPressureSensor.getLatestData().toFixed(2) : "NA"}</td>
                  </tr>` + rgbLedHtml + `
                </tbody>
              </table>
            </div>`;
  }

  propertiesButtonClick(): void {
    // FIXME: This event will not propagate to its parent. So we have to call this method here to close context menus.
    closeAllContextMenus();
    if (this.block instanceof RainbowHatBlock) {
      const d = $("#modal-dialog").html(this.getPropertiesUI());
      const rainbow = <RainbowHatBlock>this.block;

      let remoteKeyField = document.getElementById("rainbow-hat-remote-key-field") as HTMLInputElement;
      remoteKeyField.value = rainbow.getRemoteKey();
      let widthField = document.getElementById("rainbow-hat-width-field") as HTMLInputElement;
      widthField.value = Math.round(rainbow.getWidth()).toString();
      let heightField = document.getElementById("rainbow-hat-height-field") as HTMLInputElement;
      heightField.value = Math.round(rainbow.getHeight()).toString();

      const okFunction = () => {
        let success = true;
        let message;
        // set width
        let w = parseInt(widthField.value);
        if (isNumber(w)) {
          rainbow.setWidth(Math.max(20, w));
        } else {
          success = false;
          message = widthField.value + " is not a valid width";
        }
        // set height
        let h = parseInt(heightField.value);
        if (isNumber(h)) {
          rainbow.setHeight(Math.max(20, h));
        } else {
          success = false;
          message = heightField.value + " is not a valid height";
        }
        // finish
        if (success) {
          rainbow.setRemoteKey(remoteKeyField.value);
          rainbow.refreshView();
          flowchart.storeBlockStates();
          flowchart.blockView.requestDraw();
          d.dialog('close');
        } else {
          Util.showInputError(message);
        }
      };
      const enterKeyUp = (e) => {
        if (e.key == "Enter") {
          okFunction();
        }
      };
      remoteKeyField.addEventListener("keyup", enterKeyUp);
      widthField.addEventListener("keyup", enterKeyUp);
      heightField.addEventListener("keyup", enterKeyUp);

      d.dialog({
        resizable: false,
        modal: true,
        title: this.block.getUid(),
        height: 500,
        width: 400,
        buttons: {
          'OK': okFunction,
          'Close': () => d.dialog('close')
        }
      });
    }
  }

}
